/**
 * TaskListItem component for the Indigo design system.
 *
 * A bordered list row: a leading icon tile, a title + optional description, and
 * a trailing slot (typically a `StatusChip` or an action). Used for review /
 * checklist lists like "Review before appointment".
 *
 * @example
 * ```tsx
 * <TaskListItem
 *   iconName="doctor-chat"
 *   title="History of injury"
 *   description="Please compare with cover letters end to end."
 *   trailing={<StatusChip mode="dot" dotVariant="danger" label="Not started" />}
 *   onClick={openHistory}
 * />
 * ```
 */
import { clsx } from "clsx";
import {
  forwardRef,
  type KeyboardEvent,
  type MouseEventHandler,
  type ReactNode,
} from "react";
import { IconBadge } from "../IconBadge";
import type { IconProps } from "../Icons";
import { Body } from "../Typography/Body";
import { Heading } from "../Typography/Heading";
import "./TaskListItem.css";

export interface TaskListItemProps {
  /** Name of the icon shown in the leading tile. */
  iconName?: IconProps["name"];

  /** Row title. */
  title: ReactNode;

  /** Optional supporting description below the title. */
  description?: ReactNode;

  /** Trailing slot — typically a `StatusChip` or an action button. */
  trailing?: ReactNode;

  /** Click handler. When set, the row becomes an interactive button. */
  onClick?: MouseEventHandler<HTMLDivElement>;

  /** Additional CSS classes applied to the row. */
  className?: string;
}

export const TaskListItem = forwardRef<HTMLDivElement, TaskListItemProps>(
  ({ iconName, title, description, trailing, onClick, className }, ref) => {
    const interactive = Boolean(onClick);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (interactive && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onClick?.(event as unknown as Parameters<typeof onClick>[0]);
      }
    };

    return (
      <div
        ref={ref}
        className={clsx(
          "indigo-task-list-item",
          interactive && "indigo-task-list-item--interactive",
          className,
        )}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={onClick}
        onKeyDown={interactive ? handleKeyDown : undefined}
      >
        {iconName && (
          <IconBadge
            iconName={iconName}
            color="subtle"
            size={40}
            className="indigo-task-list-item__icon"
          />
        )}
        <div className="indigo-task-list-item__content">
          <Heading
            level={3}
            className="indigo-task-list-item__title text-indigo-text-secondary"
          >
            {title}
          </Heading>
          {description && (
            <Body size="md" className="text-indigo-text-tertiary">
              {description}
            </Body>
          )}
        </div>
        {trailing && (
          <div className="indigo-task-list-item__trailing">{trailing}</div>
        )}
      </div>
    );
  },
);

TaskListItem.displayName = "TaskListItem";
